import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGridPro, GridActionsCellItem, koKR } from '@mui/x-data-grid-pro';
import {
  Backdrop,
  Box,
  IconButton,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  FormControlLabel,
  Grid,
  LinearProgress,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import  {
  Add,
  ContentPaste,
  Delete,
  Edit,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Login,
  Logout,
  OpenInNew,
  Search
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { debounce } from 'lodash'; 
import dayjs from 'dayjs';
import 'dayjs/locale/ko'; // 한국어 로케일 추가
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';

import { getGridHeight } from "../../../constants/gridHeights";
import { CustomNoRowsOverlay } from "../../datagrid";
import { dateFormat } from "../../../utils";
import { FormInputText, FormInputSwitch, FormInputDate, FormInputSelect } from "../../form";
import NoticeFormDialog from "./NoticeFormDialog";
import NoticeViewDialog from "./NoticeViewDialog";
import SecurityUtil from '../../../utils/SecurityUtils';

import * as noticeStore from "../../../store/gnotice";


const GRID_HEIGHT = getGridHeight('ONE_LINE_LAYER', 54); // 0px 추가 여백
const theme = createTheme();

// 한국어 로케일 설정
dayjs.locale('ko');

const defaultValues = {
  popupYn: '',
  searchWord: "",
  startDate: "",
  endDate: "",
};

const NoticeList = ({ title }) => {

  const { handleSubmit, control, setValue, getValues } = useForm({ defaultValues: defaultValues });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loaded, setLoaded] = useState(true);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [crudMode, setCrudMode] = useState('C');
  const [rows, setRows] = useState([]);
  const searchInputRef = useRef(null);

  const sessionUser = useSelector(state => state.session.sessionUser);

  useEffect(() => {
    fetchData();

  }, []);

  const fetchData = async (searchParams) => {
    const data = await noticeStore.selectAll(searchParams);
    console.log(data);
    setRows(data);
  };

  // 검색어 검색 함수
  const debouncedSearchFn = useMemo(
    () => debounce((searchParams) => {

      fetchData(searchParams);

      // 데이터 로딩 완료 후 포커스 복원
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 300), []
  );
  
  // 검색어 입력 핸들러
  const handleChangeSearchWord = useCallback((e) => {
    const searchWord = e.target.value;
    setValue("searchWord", searchWord);
    const values = getValues();

    const searchParams = {
      startDate: values.startDate ? dayjs(values.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : '',
      endDate: values.endDate ? dayjs(values.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : '',
      searchWord: searchWord,
      popupYn: values.popupYn
    };

    debouncedSearchFn(searchParams);
  }, [debouncedSearchFn, setValue]);

  const handleChangePopupYn = (event) => {
    const value = event.target.value;
    setValue('popupYn', value);  // form 값 업데이트
    
    handleClickSearch();
  };

  const handleClickSearch = () => {
    const values = getValues();
    const searchParams = {
      startDate: values.startDate ? dayjs(values.startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss') : '',
      endDate: values.endDate ? dayjs(values.endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss') : '',
      searchWord: values.searchWord,
      popupYn: values.popupYn
    };

    console.log('Search params:', searchParams);

    fetchData(searchParams);
  };

  const handleClickOpen = () => {
    setCrudMode('C');
    setFormOpen(true);
  };

  const handleAction = async ({ type, params }) => {
    console.log(type, params);

    setOpenBackdrop(true);

    switch (type) {
      case 'detail':
        setViewOpen(true);
        setSelectedRow(params.row);
        break;
      case 'edit':
        setCrudMode('U');
        setFormOpen(true);
        setSelectedRow(params.row);
        break;
      default:
        break; 
    }

    setOpenBackdrop(false);
  };

  const handleChangePeriod = (newValue, name) => {
    console.log({ name, newValue, today: new Date() });
    
    if (!newValue) {
      return true;
    }

    if (name === "startDate") {
      const endDate = getValues("endDate");
      if (endDate && newValue > endDate) {
        setValue("endDate", newValue);
        setEndDate(newValue);
      }
      setStartDate(newValue);
    } else if (name === "endDate") {
      const startDate = getValues("startDate");
      if (startDate && newValue < startDate) {
        setValue("startDate", newValue);
        setStartDate(newValue);
      }
      setEndDate(newValue);
    }
  
    setValue(name, newValue);
    return true;
  }

  const columns = [
    {
      field: 'no',
      headerName: '번호',
      width: 20,
      valueGetter: (params) => {
        // 전체 행 수에서 현재 인덱스를 빼서 내림차순 번호 생성
        return params.api.getRowsCount() - params.api.getRowIndex(params.id);
      }
    },
    {
      field: 'title',
      headerName: '제목',
      width: 350,
    },
    {
      field: 'content',
      headerName: '내용',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const sanitizedContent = SecurityUtil.sanitizeHtml(params.value);

        return (
          <Tooltip 
            title={<span dangerouslySetInnerHTML={{ __html: sanitizedContent }} />} 
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: '540px',
                  width: '540px',
                  fontSize: '14px',
                  '& .MuiTooltip-arrow': {
                    color: 'rgba(0, 0, 0, 0.87)'
                  },
                }
              }
            }}
          >
            <Box
              component="div"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              sx={{
                whiteSpace: 'pre-wrap', // 연속된 공백, 줄바꿈 을 보존
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1, // 원하는 줄 수로 설정
                WebkitBoxOrient: 'vertical',
                '& p': {
                  margin: 0,
                  marginBlockStart: 0,
                  marginBlockEnd: 0,
                  marginInlineStart: 0,
                  marginInlineEnd: 0,
                  display: 'block'
                }
              }}
            />
          </Tooltip>
        )
      },
    },
    ...(sessionUser.type === 'ADMIN' ? [
      {
        field: 'startDate',
        headerName: '공지 시작일',
        width: 160,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => dateFormat(params.value),
      },
      {
        field: 'endDate',
        headerName: '공지 종료일',
        width: 160,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (params) => dateFormat(params.value),
      },
      {
        field: 'popupYn',
        headerName: '팝업여부',
        width: 70,
        valueGetter: (params) => params.value ? '예' : '아니오',
      },
    ] : []),
    {
      field: 'createdAt',
      headerName: '생성일시',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => dateFormat(params.value),
    },
    {
      field: 'updatedAt',
      headerName: '수정일시',
      width: 160,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => dateFormat(params.value),
    },
    { field: 'actions', 
      headerName: '기능', 
      width: 80,
      type: 'actions', 
      getActions: (params) => [
        <GridActionsCellItem
          icon={<OpenInNew fontSize="small" />}
          label="상세보기"
          onClick={() => handleAction({ type: 'detail', params })}
        />,
        ...(sessionUser.type === 'ADMIN' ? [
          <GridActionsCellItem
            icon={<Edit fontSize="small" />}
            label="수정"
            onClick={() => handleAction({ type: 'edit', params })}
          />
        ] : [])
      ]
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container component="main" maxWidth="false">
        <CssBaseline />
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={10}>
              <Grid container spacing={2} alignItems="center">
                { sessionUser.type === 'ADMIN' && (
                  <>
                    <Grid item xs={2}>
                      <FormInputDate
                        name={"startDate"}
                        control={control}
                        label={"공지 시작일"}
                        onChangePeriodValue={handleChangePeriod}
                        value={startDate}
                        getValues={getValues}
                        setValue={setValue}
                        inputHeight={37}
                        fontSize={14}
                        clearable={true}
                        iconSize={"small"}
                    />
                    </Grid>
                    <Grid item xs={2}>
                      <FormInputDate
                        name={"endDate"}
                        control={control}
                        label={"공지 종료일"}
                        onChangePeriodValue={handleChangePeriod}
                        value={endDate}
                        getValues={getValues}
                        setValue={setValue}
                        inputHeight={37}
                        fontSize={14}
                        clearable={true}
                        iconSize={"small"}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <FormInputSelect
                        name={"popupYn"}
                        control={control}
                        label={"팝업 여부"}
                        options={[
                          { value: '', label: '전체' },
                          { value: true, label: '예' },
                          { value: false, label: '아니오' }
                        ]}
                        onChange={handleChangePopupYn}
                      />
                    </Grid>
                  </>

                )}
                  <Grid item xs={4}>
                      <FormInputText
                        name={"searchWord"}
                        control={control}
                        label={"검색어(제목/내용)를 입력하세요."}
                        onCustomChange={handleChangeSearchWord}
                        inputRef={searchInputRef}
                      />
                  </Grid>
              </Grid>
            </Grid>
            <Grid item xs={2} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleClickSearch}
                sx={{  mt: 3, mb: 2, mr: 1 }}
              >
                {"검색"}
              </Button>
              { sessionUser.type === 'ADMIN' && (
                <Button
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  startIcon={<Add />}
                  onClick={handleClickOpen}
                >
                  {"등록하기"}
                </Button>
              )}
            </Grid>
          </Grid>
          <div style={{ height: GRID_HEIGHT.REDUCED, width: '100%' }}>
            <DataGridPro
              localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
              sx={{ cursor: 'pointer', fontSize: '0.85em' }}
              initialState={{ pinnedColumns: { right: ['actions'] } }}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                loadingOverlay: LinearProgress,
              }}
              headerHeight={38}
              rowHeight={34}
              loading={!loaded}
              rows={rows}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[10, 20, 50, 100]}
              pagination
              onRowDoubleClick={(params) => handleAction({ type: 'detail', params })}
            />
          </div>
        </Box>
        { formOpen && (
          <NoticeFormDialog
            crudMode={crudMode}
            open={formOpen}
            setOpen={setFormOpen}
            selectedRow={selectedRow}
            refresh={handleClickSearch}
          />
        )}
        { viewOpen && (
          <NoticeViewDialog
            open={viewOpen}
            setOpen={setViewOpen}
            selectedRow={selectedRow}
          />
        )}
      </Container>
    </ThemeProvider>
  );
};

export default NoticeList;